// Session definition (doc section 2): new session after ~30 minutes of
// inactivity. visitor_id may persist longer (localStorage), session_id
// lives in sessionStorage-like semantics but we use localStorage with an
// explicit last-activity timestamp so it also survives a same-tab
// navigation chain correctly and multiple tabs share the same session
// (per doc section 17 "multiple page tabs" — sharing one session_id across
// tabs is the simplest correct behaviour here, since sessionStorage would
// instead create a confusing *new* session per tab).

const VISITOR_KEY = "hh_visitor_id";
const SESSION_KEY = "hh_session";
const SESSION_INACTIVITY_MS = 30 * 60 * 1000;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Extremely old-browser fallback — not cryptographically strong, but
  // this is a non-sensitive pseudonymous identifier, not a security token.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function safeLocalStorage(): Storage | null {
  try {
    const testKey = "__hh_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return window.localStorage;
  } catch {
    return null; // Safari private mode, storage disabled, etc. — fail silently (doc section 1)
  }
}

let memoryFallback: Record<string, string> = {};

function getItem(key: string): string | null {
  const ls = safeLocalStorage();
  return ls ? ls.getItem(key) : memoryFallback[key] ?? null;
}
function setItem(key: string, value: string): void {
  const ls = safeLocalStorage();
  if (ls) ls.setItem(key, value);
  else memoryFallback[key] = value;
}

export interface IdentityResult {
  visitorId: string;
  sessionId: string;
  sessionStarted: boolean; // true if this call began a brand-new session
}

interface StoredSession {
  sessionId: string;
  lastActivityAt: number;
}

/** Gets (creating if necessary) the current visitor_id and session_id, rotating the session after 30 minutes idle. */
export function resolveIdentity(): IdentityResult {
  let visitorId = getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId = uuid();
    setItem(VISITOR_KEY, visitorId);
  }

  const now = Date.now();
  const rawSession = getItem(SESSION_KEY);
  let session: StoredSession | null = null;
  if (rawSession) {
    try {
      session = JSON.parse(rawSession) as StoredSession;
    } catch {
      session = null;
    }
  }

  let sessionStarted = false;
  if (!session || now - session.lastActivityAt > SESSION_INACTIVITY_MS) {
    session = { sessionId: uuid(), lastActivityAt: now };
    sessionStarted = true;
  } else {
    session.lastActivityAt = now;
  }
  setItem(SESSION_KEY, JSON.stringify(session));

  return { visitorId, sessionId: session.sessionId, sessionStarted };
}

export function touchSession(): void {
  const raw = getItem(SESSION_KEY);
  if (!raw) return;
  try {
    const session = JSON.parse(raw) as StoredSession;
    session.lastActivityAt = Date.now();
    setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

export function __resetMemoryFallbackForTests(): void {
  memoryFallback = {};
}
